import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FileXls, FilePdf, Plus, Trash, X, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError, hasRole } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useOrderFilters } from "../hooks/useOrderFilters";
import { OrdersFilters } from "../components/orders/OrdersFilters";
import { OrdersCardList } from "../components/orders/OrdersCardList";
import { OrdersTable } from "../components/orders/OrdersTable";

const BULK_STATUSES = [
  ["order_received", "Order Received"],
  ["in_production", "In Production"],
  ["ready_to_dispatch", "Ready to Dispatch"],
  ["delivered", "Delivered"],
];

export default function Orders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const createdBy = searchParams.get("created_by") || "";
  const clearCreatedBy = useCallback(() => setSearchParams({}), [setSearchParams]);
  const filters = useOrderFilters(createdBy, clearCreatedBy);
  const { params } = filters;
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [selected, setSelected] = useState([]);
  const [bulkStatus, setBulkStatus] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/orders", { params: { ...params(), page, page_size: 20 } })
      .then(({ data }) => {
        setOrders(data.orders);
        setTotal(data.total);
        setPages(data.pages);
        setSelected([]);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [params, page]);

  useEffect(() => {
    setPage(1);
  }, [params]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const exportFile = async (kind) => {
    try {
      const { data } = await api.get(`/export/${kind}`, { params: params(), responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "excel" ? "dcn_orders.xlsx" : "dcn_orders.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${kind === "excel" ? "Excel" : "PDF"} report downloaded`);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const deleteOrder = async (e, o) => {
    e.stopPropagation();
    if (!window.confirm(`Delete order ${o.order_number} (${o.customer_name})? This cannot be undone.`)) return;
    try {
      const { data } = await api.delete(`/orders/${o.id}`);
      toast.success(`Order ${o.order_number} deleted${data.renumbered ? " — sequence renumbered" : ""}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const reset = filters.reset;

  const toggleSelect = (e, id) => {
    e.stopPropagation();
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    setSelected((prev) => (prev.length === orders.length ? [] : orders.map((o) => o.id)));
  };

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.length} selected order(s)? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/orders/bulk-delete", { ids: selected });
      toast.success(`${data.deleted} order(s) deleted${data.renumbered ? " — sequence renumbered" : ""}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const bulkStatusUpdate = async () => {
    const label = BULK_STATUSES.find(([v]) => v === bulkStatus)?.[1];
    if (!label) {
      toast.error("Select a status first");
      return;
    }
    if (!window.confirm(`Change status of ${selected.length} selected order(s) to "${label}"?`)) return;
    try {
      const { data } = await api.post("/orders/bulk-status", { ids: selected, status: bulkStatus });
      const skippedNote = data.skipped_same_status ? ` (${data.skipped_same_status} already ${data.status_label})` : "";
      toast.success(`${data.updated} order(s) set to ${data.status_label}${skippedNote}`);
      setBulkStatus("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const isAdmin = hasRole(user, "admin");
  const emptyMessage = filters.isEmpty ? "No orders yet — create your first order." : "No orders match your filters.";

  return (
    <div data-testid="orders-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          {createdBy && (
            <button data-testid="orders-back-button"
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
              className="mb-2 flex items-center gap-1.5 text-sm font-medium text-slate-500 transition-colors duration-150 hover:text-slate-900">
              <ArrowLeft size={15} weight="bold" /> Back
            </button>
          )}
          <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">Orders</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <p className="text-sm text-slate-500">{total} orders</p>
            {createdBy && (
              <span data-testid="created-by-chip"
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-100 px-3 py-0.5 text-xs font-medium text-slate-700">
                Created by <span className="font-semibold">{createdBy}</span>
                <button data-testid="created-by-chip-clear" onClick={() => setSearchParams({})}
                  className="text-slate-400 transition-colors duration-150 hover:text-slate-700">
                  <X size={12} weight="bold" />
                </button>
              </span>
            )}
          </div>
        </div>
        <div className="flex w-full flex-wrap gap-2 sm:w-auto">
          <button data-testid="export-excel-button" onClick={() => exportFile("excel")}
            className="flex flex-1 items-center justify-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition-colors duration-150 hover:bg-slate-100 sm:flex-none">
            <FileXls size={16} weight="duotone" /> Excel
          </button>
          <button data-testid="export-pdf-button" onClick={() => exportFile("pdf")}
            className="flex flex-1 items-center justify-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition-colors duration-150 hover:bg-slate-100 sm:flex-none">
            <FilePdf size={16} weight="duotone" /> PDF
          </button>
          {hasRole(user, "sales", "sales_lead", "admin") && (
            <Link to="/orders/new" data-testid="create-order-button"
              className="flex w-full items-center justify-center gap-2 rounded-md bg-slate-900 px-3 py-2.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-slate-700 sm:w-auto sm:py-2">
              <Plus size={16} weight="bold" /> New Order
            </Link>
          )}
        </div>
      </div>

      <OrdersFilters search={filters.search} setSearch={filters.setSearch} status={filters.status} setStatus={filters.setStatus}
        aging={filters.aging} setAging={filters.setAging} dateFrom={filters.dateFrom} setDateFrom={filters.setDateFrom}
        dateTo={filters.dateTo} setDateTo={filters.setDateTo} onReset={reset} />

      {isAdmin && selected.length > 0 && (
        <div data-testid="bulk-actions-bar" className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm font-medium text-red-700">{selected.length} order(s) selected</p>
          <div className="flex flex-wrap items-center gap-2">
            <select data-testid="bulk-status-select" value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-700 outline-none">
              <option value="">Change status to…</option>
              {BULK_STATUSES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </select>
            <button data-testid="bulk-status-apply-button" onClick={bulkStatusUpdate} disabled={!bulkStatus}
              className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-slate-700 disabled:opacity-40">
              Update status
            </button>
            <button data-testid="bulk-clear-button" onClick={() => setSelected([])}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-600 transition-colors duration-150 hover:bg-slate-100">
              Clear
            </button>
            <button data-testid="bulk-delete-button" onClick={bulkDelete}
              className="flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-red-700">
              <Trash size={14} weight="bold" /> Delete selected
            </button>
          </div>
        </div>
      )}

      <OrdersCardList orders={orders} loading={loading} emptyMessage={emptyMessage}
        isAdmin={isAdmin} selected={selected} toggleSelect={toggleSelect} deleteOrder={deleteOrder} />

      <OrdersTable orders={orders} loading={loading} emptyMessage={emptyMessage}
        isAdmin={isAdmin} selected={selected} toggleSelect={toggleSelect}
        toggleSelectAll={toggleSelectAll} deleteOrder={deleteOrder} />

      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500" data-testid="pagination-info">
          Page {page} of {pages} · {total} orders
        </p>
        <div className="flex gap-2">
          <button data-testid="pagination-prev" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors duration-150 hover:bg-slate-100 disabled:opacity-40">
            ← Prev
          </button>
          <button data-testid="pagination-next" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors duration-150 hover:bg-slate-100 disabled:opacity-40">
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}
